// Handles ESA_OPEN_INBOX shell event by fetching pending HITL items and
// immediately starting a resolving session — bypassing the inbox list view.
// Called unconditionally at the top of EsaPanel so it stays active regardless
// of panel open/collapsed state.
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { shellEventBus } from '../../../shell/events/eventBus';
import { buildCanvasRoute } from '../../../constants/routes';
import { HIL_INBOX_FETCH_LIMIT } from '../../../constants/timing';
import {
  setResolvingTab as bridgeSetResolvingTab,
} from '../../../shell/events/resolvingTabBridge';
import { useShellStore } from '../../../shell/store/shellStore';
import { useEsaDomainStore } from '../store/esaDomainStore';
import { fetchInboxRequests } from '../services/esaApi';
import { buildResolvingTabSpec } from '../utils/buildResolvingTabSpec';
import type { InboxItem, ResolvingSession } from '../types';

export function useEsaResolveOnOpenBus(): void {
  const navigate = useNavigate();

  // Stable refs to the latest store actions — avoids re-subscribing on re-renders
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    return shellEventBus.on('ESA_OPEN_INBOX', () => {
      const {
        setEsaPanelOpen,
        setEsaPanelMode,
        setResolvingTab,
      } = useShellStore.getState();
      const {
        startResolvingSession,
        setInboxItems,
        setInboxLoading,
      } = useEsaDomainStore.getState();

      // Open panel immediately so the user sees activity
      setEsaPanelOpen(true);

      setInboxLoading('loading');
      fetchInboxRequests({ status: 'PENDING', limit: HIL_INBOX_FETCH_LIMIT, has_conversation: true })
        .then(resp => {
          const openItems: InboxItem[] = resp.items.filter(
            (i: InboxItem) => i.status === 'open',
          );

          // Persist items in the domain store so InboxView is consistent
          setInboxItems(resp.items);
          setInboxLoading('success');

          if (openItems.length === 0) {
            // No open requests — fall back to the inbox list so the user can see why
            setEsaPanelMode('inbox');
            return;
          }

          const firstItem = openItems[0];
          const session: ResolvingSession = { items: openItems, currentIndex: 0 };
          const tab = buildResolvingTabSpec(firstItem);

          startResolvingSession(session);
          setResolvingTab(tab);
          bridgeSetResolvingTab(tab);
          setEsaPanelMode('resolve');

          // Navigate to the canvas that owns the first HIL item
          navigateRef.current(buildCanvasRoute(firstItem.solutionId));
        })
        .catch(() => {
          setInboxLoading('error');
          // Fall back to inbox list so the user can retry
          setEsaPanelMode('inbox');
        });
    });
  }, []); // empty deps: shellEventBus and Zustand .getState() are singletons
}
